import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export interface RoadmapPdfColumn {
    id: string;
    header: string;
    type: 'text' | 'assign';
    position: number;
}

export interface RoadmapPdfRow {
    id: string;
    cells: Record<string, string>;
}

export interface RoadmapPdfTable {
    id: string;
    title: string;
    posX: number;
    posY: number;
    width: number;
    titleFontSize: number;
    columns: RoadmapPdfColumn[];
    rows: RoadmapPdfRow[];
    // Hauteur réellement mesurée à l'écran (px) pour ce tableau — quand fournie, le PDF étire ses
    // lignes pour occuper exactement cette hauteur, afin que l'écart avant le tableau suivant (positions
    // fixes) reste identique entre écran et PDF (remonté par Emeni : "les espacements ne sont pas les
    // mêmes"). Sans mesure, on retombe sur une hauteur calculée (comportement précédent).
    measuredHeightPx?: number;
}

export interface RoadmapPdfTextBox {
    id: string;
    content: string;
    posX: number;
    posY: number;
    width: number;
    height: number;
    fontSize: number;
    bold: boolean;
    italic: boolean;
    align: 'left' | 'center' | 'right';
}

const PX_PER_MM = 3.7795275591;
const pxToMm = (px: number) => px / PX_PER_MM;
const pxToPt = (px: number) => px * 0.75;

type RGB = [number, number, number];

// Repères reliant les rangs assignés au même salarié (voir RoadmapPage.tsx) — même palette et même
// formule de hachage qu'à l'écran, pour qu'un salarié garde exactement la même couleur de ligne entre
// l'écran et le PDF exporté. Demande d'Emeni : ces repères doivent aussi apparaître sur le PDF final.
const CONNECTOR_COLORS = ['#f97316', '#8b5cf6', '#0ea5e9', '#ec4899', '#14b8a6', '#eab308', '#6366f1', '#ef4444'];
const colorForEmployee = (empId: string) => {
    let hash = 0;
    for (let i = 0; i < empId.length; i++) hash = (hash * 31 + empId.charCodeAt(i)) | 0;
    return CONNECTOR_COLORS[Math.abs(hash) % CONNECTOR_COLORS.length];
};
const CONNECTOR_GUTTER_MM = 2.5;
const CONNECTOR_LANE_WIDTH_MM = 2.6;

const hexToRgb = (hex: string): RGB => {
    const clean = (hex || '#987b55').replace('#', '');
    const full = clean.length === 3 ? clean.split('').map(ch => ch + ch).join('') : clean;
    const n = parseInt(full, 16);
    if (isNaN(n)) return [152, 123, 85];
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const mixWhite = (rgb: RGB, ratio: number): RGB => rgb.map(c => Math.round(c + (255 - c) * ratio)) as RGB;
const mixBlack = (rgb: RGB, ratio: number): RGB => rgb.map(c => Math.round(c * (1 - ratio))) as RGB;
// Mélange deux couleurs (ratio = part de b) — sert à simuler une opacité de texte sur un fond donné,
// comme le fait déjà l'écran avec `opacity` (voir RoadmapPage.tsx, sous-titre du bandeau).
const mixColors = (a: RGB, b: RGB, ratio: number): RGB => [0, 1, 2].map(i => Math.round(a[i] + (b[i] - a[i]) * ratio)) as RGB;
// Même formule de luminance que getContrastColor (Planning/types.ts), pour rester cohérent avec le reste de l'app.
const contrastOf = (rgb: RGB): RGB => {
    const luminance = (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255;
    return luminance > 0.65 ? [0, 0, 0] : [255, 255, 255];
};

// Charge le logo en data URL via un <canvas> (plutôt qu'un fetch direct) pour rester cohérent avec la
// façon dont l'app affiche déjà le logo côté écran (balise <img>) ; en cas d'échec (CORS, logo absent,
// réseau) on renvoie simplement null et le PDF se génère sans logo plutôt que d'échouer entièrement.
const loadImageAsDataUrl = (url: string): Promise<{ dataUrl: string; width: number; height: number } | null> => {
    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;
                const ctx = canvas.getContext('2d');
                if (!ctx || !img.naturalWidth || !img.naturalHeight) { resolve(null); return; }
                ctx.drawImage(img, 0, 0);
                resolve({ dataUrl: canvas.toDataURL('image/png'), width: img.naturalWidth, height: img.naturalHeight });
            } catch (_) { resolve(null); }
        };
        img.onerror = () => resolve(null);
        img.src = url;
    });
};

export const generateRoadmapPDF = async (options: {
    companyName: string;
    brandColor: string;
    logoUrl?: string | null;
    serviceLabel: string;
    date: Date;
    orientation: 'portrait' | 'landscape';
    margins: { top: number; right: number; bottom: number; left: number };
    title: { label: string; posX: number; posY: number; fontSize: number };
    meta: { posX: number; posY: number; fontSize: number };
    tables: RoadmapPdfTable[];
    textBoxes: RoadmapPdfTextBox[];
    assignments: Record<string, Record<string, string[]>>;
    employeeNameById: Record<string, string>;
}) => {
    const { companyName, brandColor, logoUrl, serviceLabel, date, orientation, margins, title, meta, tables, textBoxes, assignments, employeeNameById } = options;

    const { jsPDF } = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');
    const logo = logoUrl ? await loadImageAsDataUrl(logoUrl) : null;

    // Palette entièrement dérivée de la couleur de marque du client (clients.theme_color) — jamais de
    // couleur en dur, comme demandé explicitement par Emeni ("les couleurs doivent toujours correspondre
    // à celui de l'établissement").
    const BRAND = hexToRgb(brandColor);
    const BRAND_TEXT = contrastOf(BRAND);
    const COLUMN_HEAD_BG = mixWhite(BRAND, 0.88);
    const COLUMN_HEAD_TEXT = mixBlack(BRAND, 0.15);
    const ROW_TEXT: RGB = [30, 41, 59];
    const BORDER = mixWhite(BRAND, 0.75);
    const COMMENT_BG = mixWhite(BRAND, 0.93);
    const COMMENT_BORDER = mixWhite(BRAND, 0.4);

    const doc = new jsPDF({ orientation, unit: 'mm', format: 'a4' });
    const pageWidthMm = doc.internal.pageSize.getWidth();
    const printableXMm = margins.left;
    const printableWidthMm = pageWidthMm - margins.left - margins.right;

    // --- Titre + sous-titre : texte simple, sans encadrement coloré (demande d'Emeni : "retire
    // l'encadré bleu dans le titre") — toujours pleine largeur imprimable, en haut de la marge, fixe
    // (non déplaçable, pour ne plus jamais chevaucher un tableau), centré.
    const titleYMm = margins.top;
    const titleLineMm = pxToMm(title.fontSize) * 1.15;
    const metaLineMm = pxToMm(meta.fontSize) * 1.3;
    const textBlockHeightMm = titleLineMm + 1.5 + metaLineMm;
    const centerXMm = printableXMm + printableWidthMm / 2;

    // Logo de l'établissement, à gauche du titre — taille généreuse de type "en-tête de document"
    // (jusqu'à 45×20mm, conservant les proportions). La première tentative calculait une taille "comme
    // sur la facture" à partir des 250×112px CSS du template de facture, mais cette conversion px→mm
    // supposait à tort que la largeur de page pilotait toute l'échelle du PDF de facture (généré par
    // capture d'écran ⁠— voir invoicePdfGenerator.ts) alors que la hauteur du contenu peut aussi être
    // la contrainte, donnant un logo bien plus petit que prévu (remonté par Emeni avec capture à
    // l'appui : "le logo ne sort pas... il est plus petit").
    const LOGO_MAX_W_MM = 45;
    const LOGO_MAX_H_MM = 20;
    let logoWidthMm = 0, logoHeightMm = 0;
    if (logo) {
        const ratio = (logo.width > 0 && logo.height > 0) ? logo.width / logo.height : 3;
        logoWidthMm = LOGO_MAX_W_MM;
        logoHeightMm = LOGO_MAX_W_MM / ratio;
        if (logoHeightMm > LOGO_MAX_H_MM) { logoHeightMm = LOGO_MAX_H_MM; logoWidthMm = LOGO_MAX_H_MM * ratio; }
    }
    const headerHeightMm = Math.max(logoHeightMm, textBlockHeightMm);
    if (logo) {
        const logoYMm = titleYMm + (headerHeightMm - logoHeightMm) / 2;
        doc.addImage(logo.dataUrl, 'PNG', printableXMm, logoYMm, logoWidthMm, logoHeightMm);
    }
    const textYMm = titleYMm + (headerHeightMm - textBlockHeightMm) / 2;

    doc.setTextColor(30, 41, 59);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(pxToPt(title.fontSize));
    doc.text(title.label, centerXMm, textYMm + titleLineMm - 1, { align: 'center' });

    doc.setFontSize(pxToPt(meta.fontSize));
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...mixColors(BRAND, [30, 41, 59] as RGB, 0.5));
    doc.text(
        `${companyName} — ${serviceLabel} — ${format(date, 'EEEE d MMMM yyyy', { locale: fr })}`,
        centerXMm,
        textYMm + titleLineMm + 1.5 + metaLineMm - 1,
        { align: 'center' }
    );
    doc.setTextColor(0, 0, 0);

    // --- Zones de texte libres : cadre bien visible (fond + bordure dans la couleur de marque, pas
    // une couleur arbitraire) pour qu'un commentaire se distingue clairement du reste de la feuille.
    textBoxes.forEach(box => {
        const xMm = pxToMm(box.posX);
        const yMm = pxToMm(box.posY);
        const wMm = pxToMm(box.width);
        const hMm = pxToMm(box.height);

        doc.setFillColor(...COMMENT_BG);
        doc.roundedRect(xMm, yMm, wMm, hMm, 2, 2, 'F');
        doc.setDrawColor(...COMMENT_BORDER);
        doc.setLineWidth(0.35);
        doc.setLineDashPattern([1.5, 1], 0);
        doc.roundedRect(xMm, yMm, wMm, hMm, 2, 2, 'S');
        doc.setLineDashPattern([], 0);

        doc.setFontSize(pxToPt(box.fontSize));
        doc.setFont('helvetica', box.bold && box.italic ? 'bolditalic' : box.bold ? 'bold' : box.italic ? 'italic' : 'normal');
        doc.setTextColor(...ROW_TEXT);
        const padMm = 3;
        const innerWidthMm = wMm - padMm * 2;
        const textXMm = box.align === 'center' ? xMm + wMm / 2 : box.align === 'right' ? xMm + wMm - padMm : xMm + padMm;
        doc.text(box.content, textXMm, yMm + padMm + pxToMm(box.fontSize), { maxWidth: innerWidthMm, align: box.align });
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
    });

    // --- Tableaux : carte arrondie, bandeau de titre dans la couleur de marque, ligne d'en-tête de
    // colonnes teintée légèrement, lignes blanches.
    tables.forEach(table => {
        const xMm = pxToMm(table.posX);
        const yMm = pxToMm(table.posY);
        const wMm = pxToMm(table.width);
        // Suit la taille du titre (comme à l'écran, où l'en-tête grandit avec le FontStepper) au lieu
        // d'une hauteur fixe — approximation du bloc "px-3 py-2" + hauteur de ligne du titre.
        const headerBarHeightMm = pxToMm(Math.max(16, table.titleFontSize * 1.25) + 17);

        doc.setFillColor(...BRAND);
        doc.roundedRect(xMm, yMm, wMm, headerBarHeightMm, 2, 2, 'F');
        // Aplatit les coins bas du bandeau (roundedRect arrondit les 4 coins) pour qu'il se raccorde
        // proprement au tableau juste en dessous.
        doc.rect(xMm, yMm + headerBarHeightMm - 2, wMm, 2, 'F');

        doc.setTextColor(...BRAND_TEXT);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(pxToPt(table.titleFontSize));
        doc.text(table.title.toUpperCase(), xMm + wMm / 2, yMm + headerBarHeightMm / 2 + 1.5, { align: 'center', maxWidth: wMm - 6 });
        doc.setTextColor(0, 0, 0);

        // Tableaux à colonnes personnalisables (demande d'Emeni) : nombre et types de colonnes variables
        // (texte libre ou assignation), plus la paire fixe Rang/Salarié assigné d'origine.
        const columns = [...table.columns].sort((a, b) => a.position - b.position);
        const body = table.rows.map(row => columns.map(col => {
            if (col.type === 'assign') {
                const empIds = assignments[row.id]?.[col.id] || [];
                return empIds.map(id => employeeNameById[id] || '').filter(Boolean).join(', ');
            }
            return row.cells[col.id] || '';
        }));

        // Largeur des colonnes de texte calée sur leur contenu réel (comme à l'écran, où le libellé est
        // en largeur naturelle, plafonnée à 35% — voir max-w-[35%] sur les cellules texte de
        // RoadmapPage.tsx), au lieu d'un ratio fixe. Un ratio fixe forçait un libellé un peu long
        // (ex. "TRIBORD / 100") à passer sur 2 lignes dans le PDF alors qu'il tenait sur une seule ligne
        // à l'écran — la ligne (et donc tout le tableau) devenait alors plus haute que prévu et
        // chevauchait le tableau suivant, positionné à une hauteur fixe. C'est la cause du chevauchement
        // remonté par Emeni ("rien ne se touche à l'écran, ça se chevauche dans le PDF"). Les colonnes
        // d'assignation se partagent le reste de la largeur à parts égales.
        const cellPaddingMm = 2.6;
        doc.setFontSize(7.5);
        const headerWidths = columns.map(col => doc.getTextWidth(col.header.toUpperCase()));
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        const textColWidthMm: Record<string, number> = {};
        columns.forEach((col, i) => {
            if (col.type !== 'text') return;
            const contentWidths = table.rows.map(r => doc.getTextWidth(r.cells[col.id] || ''));
            textColWidthMm[col.id] = Math.min(wMm * 0.35, Math.max(0, ...contentWidths, headerWidths[i]) + cellPaddingMm * 2 + 1);
        });
        const totalTextWidthMm = Object.values(textColWidthMm).reduce((a, b) => a + b, 0);
        const assignCols = columns.filter(col => col.type === 'assign');

        // Regroupe, comme à l'écran, les rangs (par index dans table.rows — même ordre que les lignes du
        // tableau imprimé) assignés au même salarié sur au moins 2 rangs, toutes colonnes d'assignation
        // confondues, puis place chaque groupe dans le premier "couloir" libre pour qu'aucune ligne ne
        // croise une autre (voir RoadmapPage.tsx pour la version écran de cet algorithme).
        const rowIndicesByEmployee: Record<string, number[]> = {};
        table.rows.forEach((row, idx) => {
            assignCols.forEach(col => {
                (assignments[row.id]?.[col.id] || []).forEach(empId => {
                    const list = (rowIndicesByEmployee[empId] ||= []);
                    if (!list.includes(idx)) list.push(idx);
                });
            });
        });
        const connectorGroups = Object.entries(rowIndicesByEmployee)
            .map(([empId, idxs]) => ({ empId, idxs: idxs.sort((a, b) => a - b), color: colorForEmployee(empId) }))
            .filter(g => g.idxs.length >= 2)
            .sort((a, b) => a.idxs[0] - b.idxs[0]);
        const laneEnds: number[] = [];
        const placedConnectors = connectorGroups.map(g => {
            const top = g.idxs[0], bottom = g.idxs[g.idxs.length - 1];
            let lane = laneEnds.findIndex(end => end < top);
            if (lane === -1) { lane = laneEnds.length; laneEnds.push(bottom); } else { laneEnds[lane] = bottom; }
            return { ...g, top, bottom, lane };
        });
        const connectorGutterWidthMm = placedConnectors.length ? CONNECTOR_GUTTER_MM + laneEnds.length * CONNECTOR_LANE_WIDTH_MM : 0;

        const remainingForAssignMm = Math.max(assignCols.length * 15, wMm - totalTextWidthMm - connectorGutterWidthMm);
        const assignColWidthMm = assignCols.length ? remainingForAssignMm / assignCols.length : 0;
        const columnStyles: Record<number, any> = {};
        columns.forEach((col, i) => {
            columnStyles[i] = col.type === 'assign'
                ? { cellWidth: assignColWidthMm, fontStyle: 'normal' }
                : { cellWidth: textColWidthMm[col.id] };
        });

        // Étire les LIGNES DE CORPS pour que la hauteur totale du tableau colle à ce qui est mesuré à
        // l'écran (bandeau de titre inclus) plutôt que de laisser autoTable calculer sa propre hauteur
        // naturelle, qui ne correspond jamais exactement au rendu CSS — c'est ce décalage qui rendait
        // l'écart avant le tableau suivant incohérent. La ligne d'en-tête de colonnes garde elle une
        // hauteur naturelle et compacte : l'étirer comme les autres lignes la rendait disproportionnée
        // (remonté par Emeni — "la ligne poste/noms doit prendre moins de hauteur").
        const headRowHeightMm = cellPaddingMm * 2 + 7.5 * 1.15 * 0.3528;
        const rowSlots = body.length || 1;
        const bodyMinCellHeightMm = table.measuredHeightPx
            ? Math.max(6, (pxToMm(table.measuredHeightPx) - headerBarHeightMm - headRowHeightMm) / rowSlots)
            : undefined;

        // Centre vertical (mm) de chaque ligne de corps, mesuré sur le rendu réel d'autoTable (les lignes
        // n'ont pas toutes la même hauteur selon leur contenu) — sert à positionner les points des repères
        // de rang exactement sur chaque ligne concernée, comme à l'écran.
        const rowCenterMmByIndex: number[] = [];
        autoTable(doc, {
            startY: yMm + headerBarHeightMm,
            margin: { left: xMm },
            tableWidth: wMm,
            head: [columns.map(col => col.header.toUpperCase())],
            body: body.length ? body : [columns.map(() => '—')],
            theme: 'grid',
            styles: { fontSize: 9, cellPadding: cellPaddingMm, lineColor: BORDER, lineWidth: 0.15 },
            headStyles: { fillColor: COLUMN_HEAD_BG, textColor: COLUMN_HEAD_TEXT, fontStyle: 'bold', fontSize: 7.5, minCellHeight: headRowHeightMm },
            bodyStyles: { fillColor: [255, 255, 255], textColor: ROW_TEXT, fontStyle: 'bold', ...(bodyMinCellHeightMm ? { minCellHeight: bodyMinCellHeightMm } : {}) },
            columnStyles,
            didDrawCell: (data) => {
                if (data.section === 'body') rowCenterMmByIndex[data.row.index] = data.cell.y + data.cell.height / 2;
            },
        });

        const finalY = (doc as any).lastAutoTable.finalY;
        doc.setDrawColor(...BORDER);
        doc.setLineWidth(0.2);
        doc.roundedRect(xMm, yMm, wMm, finalY - yMm, 2, 2, 'S');

        // Repères reliant les rangs d'un même salarié (demande d'Emeni : visibles à l'écran, doivent
        // aussi sortir sur le PDF) — même tracé que sur l'écran : une ligne verticale par groupe, dans
        // son couloir dédié, avec un point sur chaque rang concerné, dans la marge réservée à droite.
        if (placedConnectors.length && body.length) {
            const gutterStartXMm = xMm + wMm - connectorGutterWidthMm;
            placedConnectors.forEach(g => {
                const rgb = hexToRgb(g.color);
                const xLineMm = gutterStartXMm + (laneEnds.length - 1 - g.lane) * CONNECTOR_LANE_WIDTH_MM + CONNECTOR_LANE_WIDTH_MM / 2;
                const centers = g.idxs.map(idx => rowCenterMmByIndex[idx]).filter(y => y !== undefined);
                if (centers.length < 2) return;
                doc.setDrawColor(...rgb);
                doc.setLineWidth(0.5);
                doc.line(xLineMm, centers[0], xLineMm, centers[centers.length - 1]);
                doc.setFillColor(...rgb);
                centers.forEach(cy => doc.circle(xLineMm, cy, 0.7, 'F'));
            });
            doc.setDrawColor(...BORDER);
        }
    });

    return doc;
};
